import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { stageLabel } from './stage-label.js';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { ApiError, type FlagState } from '../../lib/api.js';
import { Badge } from '../../ui/Badge.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card.js';

/**
 * Which features are on for ONE account, and switching them.
 *
 * ── WHY THIS IS AN OVERRIDE AND NOT A STAGE ───────────────────────────────
 *
 * Everything here writes a per-account override, which beats the stage in both
 * directions. That is what makes this screen safe to use during a support call:
 * turning a feature off for the account in front of you does not touch the
 * rollout for anybody else, and turning one on does not require promoting it.
 *
 * The three states are genuinely three, not a checkbox. "Follows the rollout"
 * is different from "explicitly off" — the first tracks the stage as it moves,
 * the second stays off through general availability — and a two-state control
 * cannot say which one an account is in.
 */
export function AccountFlagsCard({ tenantId }: { tenantId: string }) {
  const { api, me } = useSession();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, reload } = useApiData(() => api.flags(), [api]);

  // `support` may look; changing a rollout is a production change.
  const canWrite = me?.role === 'admin' || me?.role === 'super_admin';

  async function decide(key: string, value: boolean | null) {
    setSaving(key);
    setError(null);
    try {
      await api.setFlag(key, { overrides: { [tenantId]: value } });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('flags.saveFailed'));
    } finally {
      setSaving(null);
    }
  }

  if (loading || !data) return null;

  /*
    A hosted backend owns its own console. Saying so beats rendering switches
    that write nowhere.
  */
  if (!data.managedHere) {
    return (
      <Card>
        <CardHeader bordered>
          <CardTitle>{t('flags.accountTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Banner intent="info" title={t('flags.elsewhereTitle')}>
            {t('flags.elsewhereBody', { provider: data.provider })}
          </Banner>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader bordered>
        <CardTitle>{t('flags.accountTitle')}</CardTitle>
        <CardDescription>{t('flags.accountBody')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error && <Banner intent="danger" title={t('flags.saveFailed')}>{error}</Banner>}

        {data.flags.length === 0 && <p className="text-md text-tertiary">{t('flags.none')}</p>}

        {data.flags.map((flag: FlagState) => {
          const decided = flag.overrides[tenantId];
          return (
            <div
              key={flag.key}
              className="flex flex-wrap items-center gap-3 border-b border-subtle py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                {/* The name leads; the key is the subtitle. See FlagsPage. */}
                <p className="text-md font-medium text-primary">{flag.label}</p>
                <p className="font-mono text-2xs text-tertiary">{flag.key}</p>
                <p className="text-sm text-tertiary">
                  {decided === undefined
                    ? t('flags.followsRollout', { stage: stageLabel(flag.stage) })
                    : decided
                      ? t('flags.forcedOn')
                      : t('flags.forcedOff')}
                </p>
              </div>

              <Badge intent={decided ?? flag.stage === 'general' ? 'success' : 'neutral'}>
                {decided === undefined
                  ? stageLabel(flag.stage)
                  : decided
                    ? t('flags.on')
                    : t('flags.off')}
              </Badge>

              {canWrite && (
                <div className="flex flex-wrap gap-2">
                  {/*
                    Three buttons, because there are three states. The one
                    currently in force is disabled rather than hidden, so the
                    control shows what IS as well as what could be.
                  */}
                  <Button
                    size="sm"
                    variant={decided === true ? 'primary' : 'secondary'}
                    disabled={saving === flag.key || decided === true}
                    onClick={() => void decide(flag.key, true)}
                  >
                    {t('flags.turnOn')}
                  </Button>
                  <Button
                    size="sm"
                    variant={decided === false ? 'primary' : 'secondary'}
                    disabled={saving === flag.key || decided === false}
                    onClick={() => void decide(flag.key, false)}
                  >
                    {t('flags.turnOff')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={saving === flag.key || decided === undefined}
                    onClick={() => void decide(flag.key, null)}
                  >
                    {t('flags.clear')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
